import React, { ReactNode } from 'react';
import { Modal, ModalBody, Button } from 'reactstrap';
import cn from 'classnames';

import { WorkspaceType } from 'Shared/constants/types';

import { WpButton } from 'Wpds/components/button';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Hooks
import { useOrganization } from 'Shared/utils/hooks/use-organization';

// Styles
import './SimpleAddWorkspaceWrapper.scss';

type Props = {
  title: string;
  description: string | ReactNode;
  illustration: Illustrations;
  workspaceType: WorkspaceType;
  disabledSubmit: boolean;
  submitButtonText?: string;
  isSubmitting: boolean;
  createNameSection: ReactNode;
  optionToInviteSection?: ReactNode;
  footerSection?: ReactNode;
  suggestionsSection?: ReactNode;
  onSubmit: Function;
  onClose: Function;
  createNameSectionTitle?: string;
  alert?: React.ReactNode;
};

function SimpleAddWorkspaceWrapper({
  onClose,
  title,
  description,
  disabledSubmit,
  isSubmitting,
  workspaceType,
  submitButtonText = 'Create Workspace',
  onSubmit,
  illustration,
  createNameSection,
  optionToInviteSection,
  footerSection,
  suggestionsSection,
  createNameSectionTitle,
  alert,
}: Props) {
  const isGroupWorkspace = workspaceType === WorkspaceType.isGroup;
  const { isPiDemoAccount } = useOrganization();

  return (
    <Modal centered isOpen toggle={onClose} className="simple-add-workspace-modal" zIndex="1060">
      <Button close className="simple-add-workspace-modal-close-button position-absolute" onClick={onClose} />
      <ModalBody className="simple-add-workspace-modal-content p-0">
        <div className="d-flex flex-column flex-md-row">
          <div
            className={cn(
              'simple-add-workspace-modal-content-area pt-md-5 flex-column align-items-center d-none d-md-flex',
              {
                'simple-add-workspace-modal-content-area--is-group': isGroupWorkspace,
                'simple-add-workspace-modal-content-area--is-teammate': !isGroupWorkspace,
              }
            )}
          >
            <WpIllustration
              className="simple-add-workspace-modal-content-area-image d-none d-sm-flex mb-3 mb-md-0"
              src={illustration}
            />
          </div>
          <div className="simple-add-workspace-modal-form-area py-4 py-md-5 pl-4 pl-md-5">
            <h4 className="mb-4">{title}</h4>
            {alert}
            <b>{description}</b>
            {suggestionsSection && (
              <div className={'simple-add-workspace-suggestions-on-top'}>{suggestionsSection}</div>
            )}
            {!!createNameSectionTitle && (
              <div className={'simple-add-workspace-create-name-section-title'}>
                <h5>{createNameSectionTitle}</h5>
              </div>
            )}
            {createNameSection}
            {optionToInviteSection ? (
              <>
                <div className="simple-add-workspace-modal-form-label pt-1">
                  {isGroupWorkspace ? 'Invite teammates' : 'Would you like to invite them to the workspace?'}
                </div>
                <div className="simple-add-workspace-modal-form-description mb-1">
                  You can invite them now or later on.
                </div>
                {optionToInviteSection}
              </>
            ) : null}
            <WpTooltip placement="top">
              <WpTooltipTrigger>
                <div className="py-1">
                  <WpButton
                    size="lg"
                    block
                    disabled={disabledSubmit || isPiDemoAccount}
                    loading={isSubmitting}
                    onClick={() => onSubmit()}
                  >
                    {submitButtonText}
                  </WpButton>
                </div>
              </WpTooltipTrigger>
              {isPiDemoAccount && (
                <WpTooltipContent>
                  This is for demo purposes only; demo users cannot invite users or create workspaces
                </WpTooltipContent>
              )}
            </WpTooltip>
            {footerSection}
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
}

export default SimpleAddWorkspaceWrapper;
